import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CarrierPicker } from '../../../../../components/CarrierPicker'
import { useVendors } from '../../../../../hooks/useVendors'
import { CacheKeys } from '../../../../../lib/cache'
import { toSnakeCase } from '../../../../../lib/typeHelper'
import { formatCurrency } from '../../../../../lib/utils'
import { InsurancePolicy } from '../../../../../models/insurancePolicy'
import { Vendor } from '../../../../../models/insuranceTypes'
import { CoveragesFieldset } from './CoveragesFieldset'
import {
  computeTotalPremium,
  type CoverageFormData,
  coverageToFormData,
  type FeeFormData,
  feeToFormData,
  serializeCoverages,
  serializeFees,
  STAFF_COVERAGE_TYPE_OPTIONS,
} from './insuranceFormShared'
import { OneTimeFeesFieldset } from './OneTimeFeesFieldset'

/** Parsed policy values to overlay onto the current policy (preview-changes flow). */
export interface PolicyPrefill {
  vendor: string | null
  coverages: {
    coverageType: string
    premium: number | null
    limit: number | null
    deductible: number | null
    comprehensiveDeductible?: number | null
    premiumPerUnit?: number | null
  }[]
}

function buildInitialCoverages(policy: InsurancePolicy, prefill?: PolicyPrefill | null): CoverageFormData[] {
  const coverages = policy.coverages.map(coverageToFormData)
  if (!prefill) return coverages
  // Overlay parsed values onto matching coverage types (preserving ids so they
  // update in place); add unseen coverage types as new rows.
  for (const parsed of prefill.coverages) {
    const existing = coverages.find((c) => c.coverageType === parsed.coverageType)
    if (existing) {
      existing.premium = parsed.premium
      existing.limit = parsed.limit
      existing.deductible = parsed.deductible
      existing.comprehensiveDeductible = parsed.comprehensiveDeductible ?? null
      if (parsed.premiumPerUnit != null) existing.premiumPerUnit = parsed.premiumPerUnit
    } else {
      coverages.push({
        isNew: true,
        coverageType: parsed.coverageType,
        vendorCoverageType: '',
        premium: parsed.premium,
        limit: parsed.limit,
        deductible: parsed.deductible,
        comprehensiveDeductible: parsed.comprehensiveDeductible ?? null,
        vendorPolicyId: '',
        premiumPerUnit: parsed.premiumPerUnit ?? null,
      })
    }
  }
  return coverages
}

const toDate = (value: string | null | undefined): Date | undefined => (value ? parseISO(value) : undefined)
const toIso = (date: Date | undefined): string | null => (date ? format(date, 'yyyy-MM-dd') : null)

interface Props {
  policy: InsurancePolicy
  orgId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  prefill?: PolicyPrefill | null
}

export function PolicyFormDialog({ policy, orgId, open, onOpenChange, prefill }: Props) {
  const queryClient = useQueryClient()
  const { vendors } = useVendors()
  const [vendor, setVendor] = useState<Vendor | ''>((prefill?.vendor as Vendor) || policy.vendor || '')
  const [vendorId, setVendorId] = useState<string>(policy.vendorId || '')
  const [carrierId, setCarrierId] = useState<string | null>(policy.carrierId ?? null)
  const [dateStart, setDateStart] = useState<Date | undefined>(toDate(policy.dateStart))
  const [dateEnd, setDateEnd] = useState<Date | undefined>(toDate(policy.dateEnd))
  const [coverages, setCoverages] = useState<CoverageFormData[]>(buildInitialCoverages(policy, prefill))
  const [deletedCoverageIds, setDeletedCoverageIds] = useState<string[]>([])
  const [oneTimeFees, setOneTimeFees] = useState<FeeFormData[]>(policy.oneTimeFees.map(feeToFormData))
  const [deletedOneTimeFeeIds, setDeletedOneTimeFeeIds] = useState<string[]>([])

  const newPremium = computeTotalPremium(coverages, oneTimeFees)

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      axios.patch(`/api/v1/insure/organisations/${orgId}/policies/${policy.id}/`, toSnakeCase(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
      onOpenChange(false)
    },
    onError: () => toast.error('Failed to save policy'),
  })

  function handleSave() {
    mutation.mutate({
      vendor: vendor || null,
      vendorId: vendorId || null,
      carrierId,
      dateStart: toIso(dateStart),
      dateEnd: toIso(dateEnd),
      coverages: serializeCoverages(coverages),
      deletedCoverageIds,
      oneTimeFees: serializeFees(oneTimeFees),
      deletedOneTimeFeeIds,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{prefill ? 'Review parsed policy changes' : 'Edit policy'}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-xs">Insurance provider</Label>
              <Select value={vendor} onValueChange={(value) => setVendor(value as Vendor)}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue placeholder="Select provider…" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Carrier (optional)</Label>
              <div className="mt-1">
                <CarrierPicker value={carrierId} onChange={setCarrierId} />
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Coverage start</Label>
              <div className="mt-1">
                <DateInput value={dateStart} onChange={setDateStart} />
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Coverage end</Label>
              <div className="mt-1">
                <DateInput value={dateEnd} onChange={setDateEnd} />
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Vendor ID</Label>
              <Input
                className="mt-1"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                placeholder="Policy number / vendor ID"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
              <span className="text-muted-foreground text-sm">Current premium</span>
              <span className="font-semibold">{formatCurrency(policy.premium)}</span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2">
              <span className="text-muted-foreground text-sm">New premium</span>
              <span className="font-semibold">{formatCurrency(newPremium)}</span>
            </div>
          </div>

          <CoveragesFieldset
            coverageOptions={STAFF_COVERAGE_TYPE_OPTIONS}
            value={coverages}
            onChange={setCoverages}
            onRemoveExisting={(id) => setDeletedCoverageIds((prev) => [...prev, id])}
            showPremiumPerUnit={false}
            showPolicyNumber
          />

          <OneTimeFeesFieldset
            value={oneTimeFees}
            onChange={setOneTimeFees}
            onRemoveExisting={(id) => setDeletedOneTimeFeeIds((prev) => [...prev, id])}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending}
            loading={mutation.isPending ? 'Saving...' : undefined}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
